/** Presentation only: never use these labels as source keys or record identity. */
function labelKey(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function labels(entries: readonly (readonly [string, ...string[]])[]) {
  return new Map(
    entries.flatMap(([label, ...aliases]) =>
      aliases.map((alias) => [labelKey(alias), label] as const),
    ),
  );
}

// City feeds mix readable headings, compressed database columns, and ASP.NET
// control IDs. Keep their translations here, outside collectors and snapshots.
const FIELD_LABELS = labels([
  ['Record ID', 'id', 'objectid', 'row_id', 'globalid'],
  ['Parcel number', 'blkLot', 'blocklot', 'parcel_number', 'mapblklot', 'apn'],
  ['Assessor address', 'AssessorMulti_Project_ADDRESS'],
  ['Block', 'AssessorMulti_Project_BLOCK'],
  ['Lot', 'AssessorMulti_Project_LOT'],
  ['Assessed land value', 'landval'],
  ['Assessed improvement value', 'strucval'],
  ['Assessed fixtures value', 'fixtval'],
  ['Assessed personal property value', 'othrval'],
  ['Prior sale price', 'priorprice'],
  ['Prior sale date', 'priorsaledate'],
  ['Current sale price', 'currprice'],
  ['Current sale date', 'currsaledate'],
  ['Use code', 'usecode'],
  ['Use type', 'UseClass_DESCRIPTION'],
  ['Construction code', 'construction'],
  ['Construction type', 'ConstrType_ConstrDesc', 'constrdesc'],
  ['Parcel shape', 'lotshape'],
  ['Parcel frontage', 'lotfrontage'],
  ['Parcel depth', 'lotdepth'],
  ['Parcel area', 'lotarea'],
  ['Basement area', 'basement'],
  ['Building area', 'bldgsqft'],
  ['Year built', 'yrbuilt'],
  ['Closed assessment year', 'closed_roll_year'],
  ['Exemption code', 'exemptcode'],
  ['Tax rate area', 'tra', 'taxratearea'],
  ['Neighborhood', 'analysis_neighborhood', 'neighborhoods_analysis_boundaries'],
  ['Supervisorial district', 'supervisor_district', 'supervisorial_district'],
  ['ZIP code', 'zipcode', 'zip_code'],
  ['Location', 'the_geom', 'geom', 'geometry'],
  ['Data updated', 'data_loaded_at', 'data_as_of'],
  ['Application number', 'appno', 'applicationnumber', 'applicationnum'],
  ['Permit number', 'permitno', 'permitnum', 'permitnumber'],
  ['Permit type', 'permittype'],
  ['Complaint number', 'complaintno', 'complaintnum', 'complaintnumber'],
  ['Notice of violation number', 'novno', 'novnum', 'novnumber'],
  ['Notice type', 'novtype'],
  ['Date filed', 'filedate', 'datefiled', 'fileddate'],
  ['Status date', 'statusdate'],
  ['Report date', 'reportdate'],
  ['Street number', 'streetnum', 'street_number'],
  ['Street number suffix', 'streetnumsfx', 'street_number_suffix'],
  ['Street name', 'streetname'],
  ['Street suffix', 'streetsfx', 'street_suffix'],
  ['First street number', 'street_number_low'],
  ['Last street number', 'street_number_high'],
  ['Unit suffix', 'unitsfx'],
  ['Owner or agent', 'ownername'],
  ['Compliance period', 'days'],
  ['Division', 'div'],
  ['Checked by', 'checkedby'],
  ['Review result', 'reviewresult'],
  ['Hold description', 'holddescription'],
  ['Hold started', 'inhold'],
  ['Hold ended', 'outhold'],
  ['Inspection number', 'inspection_number', 'inspectionno'],
  ['Inspection type', 'inspection_type'],
  ['Inspection description', 'inspection_type_description'],
  ['Inspection status', 'inspection_status'],
  ['Inspection started', 'inspection_start_date'],
  ['Inspection ended', 'inspection_end_date'],
  ['Inspection date', 'inspection_date'],
  ['Violation ID', 'violation_id'],
  ['Violation number', 'violation_number'],
  ['Violation date', 'violation_date'],
  ['Violation description', 'violation_item_description'],
  ['Violation code', 'violation_item'],
  ['Corrective action', 'corrective_action'],
  ['Date closed', 'close_date', 'closed_date'],
  ['Fire department occupancy number', 'occupant_number'],
  ['Agency reference', 'agency_reference'],
  ['Document link', 'documentUrl'],
  ['Source link', 'sourceUrl'],
  ['SF311 case number', 'service_request_id'],
  ['Opened', 'requested_datetime', 'openedAt'],
  ['Updated', 'updated_datetime', 'updatedAt'],
  ['Closed', 'closedAt'],
  ['Status', 'status_description'],
  ['Status notes', 'status_notes'],
  ['Responsible agency', 'agency_responsible'],
  ['Category', 'service_name'],
  ['Request type', 'service_subtype'],
  ['Request details', 'service_details'],
  ['Latitude', 'lat'],
  ['Longitude', 'long', 'lng', 'lon'],
  ['Media URL', 'media_url'],
  ['Document number', 'PrimaryDocNumber', 'documentNumber'],
  ['Old document number', 'SecondaryDocNumber'],
  ['Document date', 'DocumentDate'],
  ['Document type', 'FilingCode'],
  ['Indexed parties', 'Names'],
  ['Page count', 'NumberOfPages'],
  ['Book type', 'BookType'],
  ['Book number', 'BookNumber'],
  ['Project ID', 'ProjectID'],
  ['Project name', 'ProjectName'],
  ['Project type', 'ProjectType'],
  ['Current phase', 'CurrentPhase'],
  ['Current status', 'CurrentStatus'],
  ['Nine times permit fee', 'Ninexfee'],
  ['No penalty', 'Nopenalty'],
  ['Unsafe building', 'Unsafebldg'],
  ['Stop all work', 'Stopallwork'],
  ['Obtain a permit', 'Obtainpermit'],
  ['Work without a permit', 'Workwopermit'],
  ['Days to correct violations', 'Correctvioldays'],
  ['Days to complete work', 'Completeworkdays'],
  ['Correct the violation', 'Correctviolation'],
  ['Days to obtain a permit', 'Obtainpermitdays'],
  ['Code violation description', 'Codeviolationdesc'],
  ['File a building permit application', 'Filebldgpermitapp'],
  ['Failure to comply comments', 'Failurecommentdesc'],
  ['Days to file a building permit', 'Filebldgpermitdays'],
  ['Building permit plans required', 'Filebldgpermitplans'],
  ['Value of work without a permit', 'Workwopermitvalue'],
  ['Site contact last name', 'Sitecontactlastname'],
  ['Inspector middle initial', 'Inspector Mi'],
  ['Inspector phone', 'Inspector D Phone'],
  ['Inspector first name', 'Inspector Firstname'],
  ['Inspector last name', 'Inspector Lastname'],
  ['Reinspection date', 'Reinsp Date'],
  ['Reinspection name', 'Reinsp Name'],
  ['Reinspection phone', 'Reinsp Ph Num'],
  ['Owner address line 1', 'Owner Address1'],
  ['Owner address line 2', 'Owner Address2'],
  ['Owner address line 3', 'Owner Address3'],
  ['Owner address line 4', 'Owner Address4'],
  ['Owner ZIP code', 'Owner Zip'],
  ['Days to comply', 'Days Comp'],
  ['Source', 'Src'],
  ['Message', 'Msg'],
  ['Description', 'Desc'],
  ['Building use', 'Bldg Use'],
  ['Contractor address', 'Contractor Addr'],
  ['Assigned division', 'Assigned Div'],
  ['Received by', 'Recvd By'],
  ['License number', 'Lic No'],
  ['Form number', 'Form No'],
  ['Revision number', 'Rev#'],
  ['Addendum number', 'Addenda No.'],
  ['Plan revision number', 'Plans Revision No.'],
  ['Activity description', 'Activity Code Descr.'],
  ['Appointment time of day', 'Appointment AM/PM'],
  ['Internal status category ID', 'status_category_id_internal'],
  ['Record type', 'record_type_label'],
  ['Module name', 'module_display_name'],
  ['Module code', 'module_code'],
  ['Business name', 'business_name'],
  ['Record date', 'record_date'],
  ['Date opened', 'opened_date'],
  ['Record ID', 'guid'],
  ['Addendum description', 'Addenda Desc'],
  ['Occupancy description', 'Occupancy Desc'],
  ['Number', 'No'],
  ['Date received', "Date Rec'd"],
  ['Date sent to City Planning', 'Date to DCP'],
  ['Date received from City Planning', "Date Rec'd from DCP"],
  ['Public Works tentative approval date', 'Date DPW Tent. Approval'],
  ['Mylars received date', "Date Mylars Rec'd"],
  ['Miscellaneous exemption value', 'Misc exemption value'],
  ['Public Works surveyor', 'DPW Surveyor'],
]);

const SECTION_LABELS = labels([
  ['Case history', 'dgStatus', 'Complaint status and comments'],
  ['Permit history', 'dgStages'],
  ['Permit review details', 'dgAddendaDetails', 'Addenda Details'],
  ['Appointments', 'dgAppointmentDetails'],
  ['Inspections', 'dgInspectionDetails'],
  ['Special inspections', 'dgPtsSpInspDetails'],
  ['Complaint details', 'Complaint Data Sheet', 'ComplaintDetails', 'AddressComplaint'],
  ['Permit details', 'Permit Details Report', 'PermitDetails'],
  ['Notice of violation details', 'NOVDetails', 'Notice of Violation Details'],
  ['Building permits', 'dgBuildingPermits', 'dgBldgPermits'],
  ['Electrical permits', 'dgElectricalPermits'],
  ['Plumbing permits', 'dgPlumbingPermits'],
  ['Complaints', 'dgComplaints'],
  ['Notices of violation', 'dgNOV'],
  ['Addenda', 'grdAddenda'],
  ['Addresses', 'dgAddresses'],
  ['Site permits', 'grdSitePermit'],
  ['Permit agents', 'dgPermitAgents'],
  ['Contractor details', 'tblContractorDetails'],
  ['Case actions', 'dgActions'],
  ['Building inspection notices of violation', 'dgNOV_BID'],
  ['Housing inspection notices of violation', 'dgNOV_HIS'],
  ['Inspectors', 'dgInspector'],
  ['Violation items', 'dgItems'],
  ['Notice of violation items', 'NOV items'],
]);

const RECORD_KIND_LABELS = new Map([
  ['building', 'Building permit'],
  ['electrical', 'Electrical permit'],
  ['plumbing', 'Plumbing permit'],
  ['complaint', 'Complaint'],
  ['nov', 'Notice of violation'],
  ['assessor', 'Assessor record'],
  ['recorder', 'Recorded document'],
  ['public-works', 'Public Works permit'],
  ['subdivision', 'Subdivision project'],
  ['311', 'SF311 report'],
  ['section604', 'Section 604 affidavit'],
  ['fire-inspection', 'SFFD fire inspection'],
  ['fire-violation', 'SFFD fire violation'],
  ['fire-compliance', 'SFFD Statement of Compliance'],
  ['tax-bill', 'Property tax bill'],
]);

const ACRONYMS = new Set([
  'ADA',
  'API',
  'APN',
  'BIS',
  'CA',
  'CES',
  'DBI',
  'DCP',
  'DPW',
  'EID',
  'GIS',
  'HIS',
  'HTTP',
  'ID',
  'NOV',
  'PDF',
  'PID',
  'SF',
  'SF311',
  'SFFD',
  'URL',
  'UTC',
  'XML',
  'ZIP',
]);

function sourceLabel(value: string) {
  return value
    .replace(/^AssessorMulti_Project_/i, '')
    .replace(/^(?:(?:ctl\d+|InfoReq\d+)[_$])+/i, '')
    .replace(/^lbl(?=[A-Z_])/, '')
    .replace(/^HIS_?Complaint_?/i, '')
    .replace(/^HIS_/, '');
}

function spacedLabel(value: string) {
  const machineLabel = /[_-]|[a-z\d][A-Z]|[A-Z]{2}[a-z]|^[A-Z\d\s-]+$/.test(value);
  if (!machineLabel) return value.charAt(0).toUpperCase() + value.slice(1);
  const words = value
    .replace(/([A-Z])(?=[A-Z][a-z])/g, '$1 ')
    .replace(/([a-z\d])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return words
    .split(' ')
    .map((word, index) => {
      if (ACRONYMS.has(word.toUpperCase())) return word.toUpperCase();
      const lower = word.toLowerCase();
      return index === 0 ? lower.charAt(0).toUpperCase() + lower.slice(1) : lower;
    })
    .join(' ');
}

function singleLabel(value: string, kind: string | undefined, section: boolean) {
  // These helpers label headings, never values. Preserve references if a caller
  // passes a URL or a numeric identifier as a heading.
  if (/^[a-z][a-z\d+.-]*:\/\//i.test(value) || /^\d[\d./-]*$/.test(value)) return value;
  const source = sourceLabel(value);
  const key = labelKey(source);
  const sectionLabel = SECTION_LABELS.get(key);
  if (sectionLabel) return sectionLabel;
  if (kind === 'section604' && key === 'item') return 'Affidavit reference';
  if (kind === 'fire-compliance' && key === 'link') return 'Compliance document';
  if (kind === 'fire-compliance' && key === 'group') return 'Record category';
  if ((kind === 'complaint' || kind === 'nov') && key === 'num') return 'Complaint number';
  const field = FIELD_LABELS.get(labelKey(value)) ?? FIELD_LABELS.get(key);
  if (field) return field;
  // Unknown ASP.NET grid names still get a useful heading without control IDs.
  const readable = spacedLabel(source.replace(/^(?:dg|grd|tbl)(?=[A-Z])/, ''));
  return readable || (section ? 'Details' : 'Field');
}

function displayLabel(value: string, kind: string | undefined, section: boolean) {
  const trimmed = value.trim();
  if (!trimmed) return section ? 'Details' : 'Field';
  if (/^[a-z][a-z\d+.-]*:\/\//i.test(trimmed)) return value;
  return trimmed
    .replace(/\s+/g, ' ')
    .split(' · ')
    .map((part) => {
      // Keep positional context and event suffixes when displaying saved diffs.
      if (
        /^(?:row|entry) (?:added|removed)$/.test(part) ||
        /^(?:Page|Table|Column|Assessment) \d+$/.test(part)
      )
        return part;
      return singleLabel(part, kind, section);
    })
    .join(' · ');
}

export function propertyFieldLabel(value: string, kind?: string) {
  return displayLabel(value, kind, false);
}

export function propertySectionLabel(value: string, kind?: string) {
  return displayLabel(value, kind, true);
}

export function propertyRecordKindLabel(kind: string) {
  return RECORD_KIND_LABELS.get(kind.toLowerCase()) ?? propertyFieldLabel(kind || 'Record');
}

/** How Ava names a source in running text; a descriptor's own label wins over these. */
const SOURCE_LABELS = new Map([
  ['dbi', 'Building Inspection'],
  ['assessor', 'Assessor property data'],
  ['recorder', 'Recorder documents'],
  ['subdivision', 'subdivision projects'],
  ['sf311', 'SF311 service reports'],
  ['section604', 'Section 604 affidavits'],
  ['fire-inspections', 'fire inspections'],
  ['fire-violations', 'fire violations'],
  ['fire-compliance', 'fire compliance statements'],
  ['public-works', 'Public Works permits'],
  ['identity', 'parcel identity'],
  ['street-view', 'Street View'],
  ['tax-bills', 'tax bills'],
]);
export function propertySourceLabel(source: string) {
  return SOURCE_LABELS.get(source.toLowerCase()) ?? propertyFieldLabel(source);
}

/** The nouns for a property summary's record counts: singular and plural. */
const COUNT_NOUNS = new Map<string, [string, string]>([
  ['permits', ['permit', 'permits']],
  ['activePermits', ['active permit', 'active permits']],
  ['complaints', ['complaint', 'complaints']],
  ['novs', ['notice of violation', 'notices of violation']],
  ['sf311Reports', ['311 report', '311 reports']],
  ['openFireViolations', ['open fire violation', 'open fire violations']],
  ['recordedDocuments', ['recorded document', 'recorded documents']],
]);
export function propertyCountNoun(key: string, count: number) {
  const [singular, plural] = COUNT_NOUNS.get(key) ?? [
    propertyFieldLabel(key).toLowerCase(),
    `${propertyFieldLabel(key).toLowerCase()}s`,
  ];
  return `${count} ${count === 1 ? singular : plural}`;
}

/**
 * The name a chip gives one record. Recorded documents use the Recorder's
 * own document type, since "Recorded document" says nothing about what it is.
 */
export function propertyRecordChipLabel(
  record: { kind: string; description?: string | null },
  fallbackKindLabel = propertyRecordKindLabel(record.kind),
) {
  const title = record.kind === 'recorder' ? record.description?.split(',')[0]?.trim() : undefined;
  return title ? propertyFieldLabel(title) : fallbackKindLabel;
}

const GENERIC_DESCRIPTIONS = new Set([
  'open the official dbi record for full details',
  'open the official record for full details',
  'see official record for details',
  'no description available',
  'no description provided',
  'inspectors comment',
  "inspector's comment",
  'inspector comments',
  'inspectors comments',
]);

export function propertyRecordTitle(
  record: { id: string; kind: string; description?: string | null },
  fallbackKindLabel = propertyRecordKindLabel(record.kind),
) {
  const description = record.description?.trim() ?? '';
  if (description && !GENERIC_DESCRIPTIONS.has(description.toLowerCase().replace(/[.!]$/, ''))) {
    return record.description!;
  }
  return record.id ? `${fallbackKindLabel} · ${record.id}` : fallbackKindLabel;
}
